<template>
  <div class="main">

    <!-- loading -->
    <transition name="fade">
      <v-progress-linear
        v-if="isLoading"
        class="progress-linear--fixed"
        color="warning"
        height="3"
        indeterminate
      />
    </transition>

    <!-- success -->
    <transition name="fade">
      <div v-if="isSuccess" class="main__container">
        <div v-if="receipts.length">
          <div v-for="(receipt, index) in receipts" :key="`receipt-${index}`">
            <div class="content">
              <section
                v-for="(ride, index) in receipt.reportTaxiJob"
                :key="ride.apiTaxiJobId"
              >
                <header
                  v-if="index % 2 === 0"
                  class="header"
                  :class="[
                    { 'print': index === 0 },
                    { 'no-print': index > 0 },
                  ]"
                >
                  <img src="@/assets/img//logo-99-empresas-82x68.png" alt="">
                  <h1>{{ $t(`${namespace}.corporateRaceVoucher`) }}</h1>
                  <p>
                    <strong>{{ receipt.companyName }}</strong><br>
                    {{ $t(`${namespace}.period`) }}: {{ moment(startDate).format('DD/MM/YYYY') }} - {{ moment(endDate).format('DD/MM/YYYY') }}
                  </p>
                </header>
                <div
                  class="section"
                  :class="{ 'section--border-bottom': index % 2 === 0 }"
                >
                  <h2><strong>{{ $t(`${namespace}.rideValue`) }}: R$ {{ ride.jobPrice }}</strong></h2>
                  <ul>
                    <li>
                      <strong>{{ $t(`${namespace}.passenger`) }}:</strong>&nbsp;<span class="text-capitalize">{{ ride.employeeName | textLowercase }}</span>
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.phone`) }}:</strong> {{ ride.employeePhone | phoneWithDDD }}
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.ride`) }} #:</strong> {{ ride.apiTaxiJobId }}
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.origin`) }}:</strong>&nbsp;
                      <a
                        :href="`https://www.google.com.br/maps?q=${ride.startLatitude},${ride.startLongitude}`"
                        :title="$t(`${namespace}.origin`)"
                        target="_blank"
                      >
                        {{ ride.startAddress }}
                      </a>
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.destiny`) }}:</strong>&nbsp;
                      <a
                        :href="`https://www.google.com.br/maps?q=${ride.endLatitude},${ride.endLongitude}`"
                        :title="$t(`${namespace}.destiny`)"
                        target="_blank"
                      >
                        {{ ride.endAdress }}
                      </a>
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.startDate`) }}:</strong> {{ moment(ride.startDate).format('DD/MM/YYYY HH:mm:ss') }}
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.endDate`) }}:</strong> {{ moment(ride.endDate).format('DD/MM/YYYY HH:mm:ss') }}
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.costCenter`) }}:</strong> {{ ride.costCenter }}
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.project`) }}:</strong> {{ ride.project }}
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.reason`) }}:</strong> {{ ride.reason | normalizeStr }}
                    </li>
                    <li v-if="ride.driverName">
                      <strong>{{ $t(`${namespace}.driver`) }}:</strong>&nbsp;<span class="text-capitalize">{{ ride.driverName | textLowercase }}</span>
                    </li>
                    <li v-if="ride.driverPhone">
                      <strong>{{ $t(`${namespace}.phone`) }}:</strong> {{ ride.driverPhone | phoneWithDDD  }}
                    </li>
                    <li v-if="ride.carModel">
                      <strong>{{ $t(`${namespace}.carModel`) }}:</strong> {{ ride.carModel }}
                    </li>
                    <li v-if="ride.carPlate ">
                      <strong>{{ $t(`${namespace}.carPlate`) }}:</strong> {{ ride.carPlate }}
                    </li>
                    <li>
                      <strong>{{ $t(`${namespace}.city`) }}:</strong> {{ ride.city }}
                    </li>
                  </ul>
                </div>

                <div v-if="index % 2 !== 0" class="page-break" />
              </section>
            </div>
          </div>
        </div>

        <EmptyState
          v-else
          class="mb-5"
          image="rides"
          :text="$t('Components.ReportRides.noneFound')"
        />
      </div>
    </transition>

    <!-- error -->
    <transition name="fade">
      <v-layout
        v-if="isFailed"
        v-layout
        align-center
        justify-center
        fill-height
        row
      >
        <span class="text-xs-center mb-5">
          {{ $t('Common.genericErrorMsg') }}<br>
          <a
            href=""
            :title="$t('Common.tryAgain')"
            class="text--link accent--text"
            @click.prevent="tryAgain"
          >
            {{ $t('Common.tryAgain') }}.
          </a>
        </span>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isObject } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'ReceiptsPage',
  components: {
    EmptyState
  },
  filters: {
    normalizeStr (str) {
      return str.replace(/\+/g, ' ')
    }
  },
  computed: {
    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.ReceiptsPage',
      currentStatus: null,
      startDate: null,
      endDate: null,
      receipts: []
    }
  },
  methods: {
    isValidObject (val) {
      return isObject(val)
    },

    tryAgain () {
      window.location.reload()
    },

    async getReceipts () {
      this.currentStatus = STATUS_IS_LOADING

      try {
        const {
          startDate,
          endDate,
          category,
          companyId,
          downloadType,
          employeeId,
          projectId,
          supervisorId
        } = this.$route.query

        const { data: res } = await this.$apiService.getReceipts(
          { endDate },
          { startDate },
          { category },
          { companyId },
          { downloadType },
          { employeeId },
          { projectId },
          { supervisorId }
        )

        this.startDate = res.data.startDate
        this.endDate = res.data.endDate
        this.receipts = res.data.report
        this.currentStatus = STATUS_SUCCESS
        // setTimeout(() => print(), 1000)
      } catch (err) {
        console.log('Error', err)
        this.currentStatus = STATUS_FAILED
      }
    }
  },
  mounted () {
    if (!this.isValidObject(this.$route.query)) {
      this.$router.push({ name: 'Reports' })
      return
    }

    this.getReceipts()
  }
}
</script>

<style lang="scss" scoped>
.main,
.main__container {
  height: 100%;
}

body {
  color: $gray-8;
  font-family: Helvetica, Arial, sans-serif;
  line-height: 1.4em;
}

a {
  color: $accent;
}

.main {
  margin: 0 auto;
  max-width: 780px;
}

.header {
  padding: 30px 0 5px;
  text-align: center;

  img {
    margin-bottom: 20px;
    width: 52px;
  }

  h1 {
    font-size: 20px;
    margin-bottom: 5px;
  }

  p { font-size: 13px; }
}

.section {
  padding: 25px 0;

  h2 {
    font-size: 16px;
    margin-bottom: 10px;
  }

  ul {
    list-style-type: none;
    padding: 0;
  }

  li {
    font-size: 13px;
  }
}

.section--border-bottom {
  border-bottom: 1px solid $gray-4;
}

.print {
  display: block;
}

.no-print {
  display: none;
}

@media print {
  @page { margin: 0.5cm; }

  * {
    background: transparent !important;
    color: $gray-8 !important;
    filter: none !important;
    text-shadow: none !important;
  }

  body {
    font-size: 14px;
    margin: 0;
    padding: 0;
  }

  .main {
    margin: 0 auto;
    max-width: 100%;
    padding: 0 25px;
  }

  a {
    text-decoration: none;
  }

  .page-break {
    page-break-after: always;
  }

  .print,
  .no-print {
    display: block;
  }
}
</style>

<style lang="scss">
@media print {
  .logged .v-content {
    padding-top: 0 !important;
  }

  .chat,
  .cx-widget {
    display: none !important;
  }
}
</style>
